import { Tab } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  ActivacionInstallOTClientDataFormTab,
  //SaveFormDataActivacionInstallPendingOT,
} from '@/erp/operaciones/activaciones/instalaciones/shared/components';
import { ImageListModal } from '@/shared/components/ui/Images';
import {
  EstadosOrdenTrabajoEnumChoice,
  ROUTER_PATHS,
  a11yProps,
  useTabsFormOnly,
} from '@/shared';
import {
  ApproveRejectInstallOTData,
  RecoordinarInstallOTData,
  useUpdateOrdenTrabajo,
} from '@/shared/actions/erp';
import {
  CustomButtunsRejectConfirm,
  CustomTabPanel,
  CustomTextArea,
  CustomTextAreaNoForm,
  FormTabsOnly,
  TabTextLabel,
  TabsFormBoxScene,
} from '@/shared/components/ui';
import { AlertSeverityEnum, OrdenTrabajo } from '@/shared/interfaces';
import { useUiConfirmModalStore, useUiStore } from '@/store/ui';
import OperacionPrerechazadaInstallDetail from './OperacionPrerechazadaInstallDetail';

export type SaveOperacionTenicoPreRechazadoProps = {
  titlePageNode?: React.ReactNode;
  titlePage?: string;
  ordenTrabajo: OrdenTrabajo;

  customReturnUrl?: string;
};

/* export type SaveOperacionTenicoPreRechazadoForm =
  SaveFormDataActivacionInstallPendingOT & {}; */


const returnUrlOperacionTecnicoPage =
  ROUTER_PATHS.operaciones.tecnicosInstalacionNav;

const SaveOperacionTenicoPreRechazado: React.FC<
  SaveOperacionTenicoPreRechazadoProps
> = ({
  titlePageNode,
  titlePage,
  ordenTrabajo,
  customReturnUrl = returnUrlOperacionTecnicoPage,
}) => {
    ///* hooks ---------------------
    const navigate = useNavigate();
    const { handleMethodChange, valueTab } = useTabsFormOnly();

    ///* global state ---------------------
    const setAlert = useUiStore(state => state.setAlert);
    const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
    const setConfirmDialogIsOpen = useUiConfirmModalStore(
      s => s.setConfirmDialogIsOpen
    );

    ///* form ------------------------
    /* const form = useForm<SaveOperacionTenicoPreRechazadoForm>({}); */
    const form = useForm<any>({});

    ///* mutations ---------------------
    const approveRejection = useUpdateOrdenTrabajo<ApproveRejectInstallOTData>({
      navigate,
      returnUrl: customReturnUrl,
      customSuccessMessage: 'Rechazo de la instalación aprobado correctamente.',
    });
    const recoordinarInstall = useUpdateOrdenTrabajo<RecoordinarInstallOTData>({
      navigate,
      returnUrl: customReturnUrl,
      customSuccessMessage: 'Recoordinación de la instalación exitosa.',
    });

    ///* handlers ---------------------
    const onConfirmRejection = async () => {
      setConfirmDialog({
        isOpen: true,
        title: 'Aprobar rechazo de la instalación',
        subtitle: '¿Está seguro de aprobar el rechazo de la instalación?',
        onClose: () => {
          setConfirmDialogIsOpen(false);
        },
        onConfirm: () => {
          setConfirmDialogIsOpen(false);
          approveRejection.mutate({
            id: ordenTrabajo?.id_orden_trabajo!,
            data: {
              estado_orden_trabajo: EstadosOrdenTrabajoEnumChoice.RECHAZADA,
            },
          });
        },
        confirmTextBtn: 'SI',
        btnTitle: 'NO',
      });
    };

    const onRecoordination = () => {
      setConfirmDialog({
        isOpen: true,
        title: 'Recoordinar instalación',
        subtitle:
          '¿Está seguro de recoordinar la instalación? Esta acción creará una nueva agenda para la instalación.',
        onClose: () => {
          setConfirmDialogIsOpen(false);
        },
        onConfirm: () => {
          setConfirmDialogIsOpen(false);
          recoordinarInstall.mutate({
            id: ordenTrabajo?.id_orden_trabajo!,
            data: {
              estado_orden_trabajo: EstadosOrdenTrabajoEnumChoice.RECHAZADA,
              recoordination_required: true,
            },
          });
        },
        confirmTextBtn: 'SI, RECOORDINAR',
        btnTitle: 'NO',
      });
    };

    ///* effects ------------------------
    useEffect(() => {
      if (!ordenTrabajo?.id_orden_trabajo) return;

      const { prospecto_data, preventa_data, agendamiento_data, ...rest } =
        ordenTrabajo;

      ///* reset form -------------
      form.reset({
        ...prospecto_data,
        ...preventa_data,
        ...agendamiento_data,
        ...rest,
        planLabel: ordenTrabajo?.plan_data?.nombre || '',
        flotaLabel: ordenTrabajo?.flota_data?.nombre || '',
        tipo_instalacion: preventa_data?.tipo_instalacion || '',
        valor_instalacion: preventa_data?.valor_instalacion || '',

        // observacion_activacion:
        //   ordenTrabajo?.observaciones.observacion_activacion,

        observacion_1: ordenTrabajo?.preventa_data?.observaciones?.observacion_1,
        observacion_2: ordenTrabajo?.preventa_data?.observaciones?.observacion_2,
        observacion_llamada:
          agendamiento_data?.observaciones?.observacion_llamada,

        condicion_residencia: preventa_data?.condicion_residencia || '',

        numero_documento: rest.contrato_data?.numero_referencia,

        velocidad_internet: preventa_data?.velocidad
          ? `${preventa_data?.velocidad} ${rest?.plan_data?.unidad}`
          : '',

        //
        ciudad: rest.ciudad_data?.nombre,
        zona: rest.zona_data?.nombre,
        sector: rest.sector_data?.nombre,
        rawNap: rest.nap_data,
        distancia: agendamiento_data?.distancia || '0',
        puerto: rest.puerto!,
        nodo: rest.nodo!,

        hora_inicio: rest?.hora_inicio
          ? dayjs(rest?.hora_inicio, 'HH:mm:ss')
          : null,
        hora_fin: rest?.hora_fin ? dayjs(rest?.hora_fin, 'HH:mm:ss') : null,
        observacion_activacion: rest?.observaciones?.observacion_activacion,
        coordenadas: rest?.coordenadas,
        /* } as unknown as SaveFormDataActivacionInstallPendingOT); */
      } as unknown as any);

      ///* set equipos disponibles -------------
    }, [ordenTrabajo, form.reset]);

    return (
      <TabsFormBoxScene
        titlePage={titlePage!}
        titlePageNode={titlePageNode}
        showBtns={false}
        // // custom btns
        showCustomBtns={true}
        customBtns={
          <CustomButtunsRejectConfirm
            confirmTextBtn="Aprobar"
            rejectTextBtn="Recoordinar"
            onCancel={() => {
              navigate(customReturnUrl);
            }}
            // ---------- CONFIRM REJECT: kill process ----------
            onConfirm={form.handleSubmit(onConfirmRejection, () => {
              setAlert(
                true,
                'Debe completar los campos requeridos.',
                AlertSeverityEnum.error
              );
            })}
            // ---------- RECOORDINAR | Create new Agenda - ESPERA_REAGENDAR ----------
            onReject={onRecoordination}
          />
        }
        tabs={
          <FormTabsOnly onChange={handleMethodChange} value={valueTab}>
            <Tab label="DATOS GENERALES CLIENTE" value={1} {...a11yProps(1)} />
            <Tab label="DETALLE ACTIVACIÓN" value={2} {...a11yProps(2)} />
            <Tab label="MOTIVO RECHAZO" value={3} {...a11yProps(3)} />
          </FormTabsOnly>
        }
      >
        {/* ============ DATOS GENERALES CLIENTE ============ */}
        <CustomTabPanel index={valueTab} value={1}>
          <ActivacionInstallOTClientDataFormTab form={form} />
        </CustomTabPanel>

        {/* ============ DETALLE ACTIVACIÓN ============ */}
        <CustomTabPanel index={valueTab} value={2}>
          <OperacionPrerechazadaInstallDetail
            ordenTrabajo={ordenTrabajo}
            form={form}
          />
        </CustomTabPanel>

        {/* ============ MOTIVO RECHAZO ============ */}
        <CustomTabPanel index={valueTab} value={3}>
          <CustomTextArea
            label="Motivo Rechazo"
            name="motivo_rechazo"
            control={form.control}
            defaultValue={form.getValues().motivo_rechazo || ''}
            error={undefined}
            helperText={
              form.formState.errors.motivo_rechazo?.message?.toString() ?? ''
            }
            required
            disabled
          />
          <CustomTextAreaNoForm
            label="Observación Rechazo"
            value={ordenTrabajo?.observaciones?.observacion_rechazo_tecnico || ''}
            required
            disabled
          />

          <TabTextLabel text="Fotos" pt="42px" />
          <ImageListModal
            images={[
              {
                id: "1",
                img: ordenTrabajo?.urls_fotos_rechazo_tecnico?.[0].photoUrl || '',
                title: 'Evidencia 1',
              },
              {
                id: "2",
                img: ordenTrabajo?.urls_fotos_rechazo_tecnico?.[1].photoUrl || '',
                title: 'Evidencia 2',
              },
            ]}
          />
        </CustomTabPanel>
      </TabsFormBoxScene>
    );
  };

export default SaveOperacionTenicoPreRechazado;
